import { MercatorCoordinate, type Map as MapLibreMap } from 'maplibre-gl';
import { projectedCalloutProxy } from './callout';
import { nearestWorldLongitude } from '../map/worldViewport';
import type { Annotation, AnnotationCoordinate, AnnotationMovePart } from './model';
import type { AnnotationScreenPoint, AnnotationScreenProjection } from './textLayout';

/** Missing authored fields are the legacy map plane. Heights never affect legend/style sizes. */
export function annotationHeight(annotation: Annotation, part?: AnnotationMovePart): number {
  if (annotation.type === 'callout')
    return part === 'target' ? 0 : (annotation.labelHeightAboveMapM ?? 0);
  return annotation.type === 'marker' || annotation.type === 'text' || annotation.type === 'line'
    ? (annotation.heightAboveMapM ?? 0)
    : 0;
}

export interface ElevationCamera {
  readonly center: AnnotationCoordinate;
  readonly zoom: number;
  readonly pitch: number;
  readonly bearing: number;
  readonly roll: number;
  readonly verticalFieldOfView: number;
  readonly width: number;
  readonly height: number;
  readonly padding: {
    readonly left: number;
    readonly right: number;
    readonly top: number;
    readonly bottom: number;
  };
}

export interface ElevatedAnnotationProjection extends AnnotationScreenProjection {
  readonly projectAtHeight: (
    coordinate: AnnotationCoordinate,
    heightM: number,
  ) => AnnotationScreenPoint | null;
  readonly unprojectAtHeight: (
    point: AnnotationScreenPoint,
    heightM: number,
  ) => AnnotationCoordinate | null;
}

/**
 * Mercator perspective using public camera values, matching MapLibre 6.5's 512-pixel world and
 * vertical FOV. Ground projection stays delegated to MapLibre (including padding/roll).
 * No renderer internals, camera-dependent authored coordinates, or pixel-height approximation.
 */
export function elevationProjection(
  ground: AnnotationScreenProjection,
  camera: ElevationCamera,
): ElevatedAnnotationProjection {
  const radians = Math.PI / 180;
  const pitch = camera.pitch * radians,
    bearing = camera.bearing * radians,
    roll = camera.roll * radians;
  const world = 512 * 2 ** camera.zoom;
  const distance = camera.height / 2 / Math.tan((camera.verticalFieldOfView * radians) / 2);
  const centre = MercatorCoordinate.fromLngLat([...camera.center]);
  const screenCentre = {
    x: (camera.width + camera.padding.left - camera.padding.right) / 2,
    y: (camera.height + camera.padding.top - camera.padding.bottom) / 2,
  };
  const cp = Math.cos(pitch),
    sp = Math.sin(pitch),
    cb = Math.cos(bearing),
    sb = Math.sin(bearing);
  const cr = Math.cos(roll),
    sr = Math.sin(roll);
  const projectAtHeight = (
    coordinate: AnnotationCoordinate,
    heightM: number,
  ): AnnotationScreenPoint | null => {
    // The v1 plan-view contract deliberately keeps height invisible in a north-down view.
    if (heightM === 0 || camera.pitch === 0) return ground.project(coordinate);
    const position = MercatorCoordinate.fromLngLat([...coordinate], heightM);
    const dx = (position.x - centre.x - Math.round(position.x - centre.x)) * world;
    const dy = (position.y - centre.y) * world;
    const z = position.z * world;
    const x = cb * dx + sb * dy;
    const y = cp * (-sb * dx + cb * dy) - sp * z;
    const depth = distance - sp * (-sb * dx + cb * dy) - cp * z;
    if (depth <= 1 || !Number.isFinite(depth)) return null;
    return {
      x: screenCentre.x + (distance * (cr * x + sr * y)) / depth,
      y: screenCentre.y + (distance * (-sr * x + cr * y)) / depth,
    };
  };
  const unprojectAtHeight = (
    point: AnnotationScreenPoint,
    heightM: number,
  ): AnnotationCoordinate | null => {
    if (heightM === 0 || camera.pitch === 0) return ground.unproject(point);
    const coordinate = ground.unproject(point);
    const q = MercatorCoordinate.fromLngLat([...coordinate]);
    // Camera-to-ground ray. A constant metre height has latitude-dependent conformal Z;
    // iterate that scale instead of changing the authored height during drag.
    const cx = centre.x - (distance / world) * sp * sb;
    const cy = centre.y + (distance / world) * sp * cb;
    const cz = (distance / world) * cp;
    let result = coordinate;
    for (let iteration = 0; iteration < 12; iteration += 1) {
      const z = MercatorCoordinate.fromLngLat([...result], heightM).z;
      const t = 1 - z / cz;
      if (t <= 0 || !Number.isFinite(t)) return null;
      const position = new MercatorCoordinate(cx + (q.x - cx) * t, cy + (q.y - cy) * t).toLngLat();
      result = [position.lng, position.lat];
    }
    const projected = projectAtHeight(result, heightM);
    return projected && Math.hypot(projected.x - point.x, projected.y - point.y) < 0.05
      ? result
      : null;
  };
  const billboardScaleAt = (coordinate: AnnotationCoordinate): number => {
    const position = MercatorCoordinate.fromLngLat([...coordinate]);
    const dx = (position.x - centre.x - Math.round(position.x - centre.x)) * world;
    const dy = (position.y - centre.y) * world;
    const depth = distance - sp * (-sb * dx + cb * dy);
    // MapLibre 6.5's viewport-facing symbol shader multiplies by this clamped perspective ratio.
    // Counter it only for callout billboards so the displayed box matches shared screen geometry.
    const ratio = Math.min(4, Math.max(0, 0.5 + (0.5 * distance) / depth));
    return depth > 0 && Number.isFinite(ratio) && ratio > 0 ? 1 / ratio : 1;
  };
  return { ...ground, projectAtHeight, unprojectAtHeight, billboardScaleAt };
}

export function mapAnnotationProjection(map: MapLibreMap): ElevatedAnnotationProjection {
  const ground: AnnotationScreenProjection = {
    project: (coordinate) =>
      map.project([nearestWorldLongitude(coordinate[0], map.getCenter().lng), coordinate[1]]),
    unproject: (point) => {
      const coordinate = map.unproject([point.x, point.y]);
      return [coordinate.lng, coordinate.lat];
    },
  };
  const canvas = map.getCanvas();
  const centre = map.getCenter();
  return elevationProjection(ground, {
    center: [centre.lng, centre.lat],
    zoom: map.getZoom(),
    pitch: map.getPitch(),
    bearing: map.getBearing(),
    roll: map.getRoll(),
    verticalFieldOfView: map.getVerticalFieldOfView(),
    width: canvas.clientWidth,
    height: canvas.clientHeight,
    padding: { left: 0, right: 0, top: 0, bottom: 0, ...map.getPadding() },
  });
}

/**
 * Camera-derived render proxy only: native sources consume the exact projected 3D geometry.
 * This preserves existing symbol typography, strokes, endpoints, ordering and rendered queries.
 * Zero height returns the original object. Off-camera / beyond-ground-horizon content is omitted.
 */
export function annotationRenderProxy(
  annotation: Annotation,
  projection: AnnotationScreenProjection,
): Annotation | null {
  const height = annotationHeight(annotation);
  if (!height) return annotation;
  if (!('projectAtHeight' in projection)) return null;
  const elevated = projection as ElevatedAnnotationProjection;
  const proxy = (coordinate: AnnotationCoordinate): AnnotationCoordinate | null => {
    const point = elevated.projectAtHeight(coordinate, height);
    if (!point) return null;
    const result = projection.unproject(point);
    const check = projection.project(result);
    return Number.isFinite(result[0]) &&
      Number.isFinite(result[1]) &&
      Math.hypot(check.x - point.x, check.y - point.y) < 0.05
      ? result
      : null;
  };
  if (annotation.type === 'marker' || annotation.type === 'text') {
    const position = proxy(annotation.position);
    return position ? { ...annotation, position } : null;
  }
  if (annotation.type === 'callout') {
    const textPosition = projectedCalloutProxy(annotation, projection);
    return textPosition ? { ...annotation, textPosition } : null;
  }
  if (annotation.type === 'line') {
    const coordinates = annotation.coordinates.map(proxy);
    return coordinates.every((coordinate) => coordinate !== null)
      ? { ...annotation, coordinates }
      : null;
  }
  return annotation;
}
